import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Container,
  Box,
  Checkbox,
  IconButton,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import productMappingApi from "../../../../services/product_mapping";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../../../redux/loader/actions";
import { showSnackbar } from "../../../../redux/snackbar/actions";

const ProductList = ({
  planDetails,
  handleProductChange,
  selectedProduct,
  uuid,
}) => {
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [updatedPlans, setUpdatedPlans] = useState([]);
  const menuItems = useSelector((state) => state.menuReducer.items);
  const createMenuItem = menuItems
    ?.find((item) => item.name.toLowerCase() === "products")
    ?.submenus?.find((item) => item.name.toLowerCase() === "mapping");
  const isDisabled = createMenuItem?.access_type === "read_write";

  const commonFeatures =
    Array.isArray(planDetails) && planDetails.length > 0
      ? planDetails[0].features
      : [];

  useEffect(() => {
    if (Array.isArray(planDetails)) {
      const initialUpdatedPlans = planDetails.map((plan) => ({
        plan_uuid: plan.plan_uuid,
        plan_name: plan.plan_name,
        plan_status: plan.plan_status,
        features: plan.features.map((feature) => ({
          module_uuid: feature.module_uuid,
          module_name: feature.module_name,
          is_available: feature.is_available,
        })),
      }));
      setUpdatedPlans(initialUpdatedPlans);
    }
  }, [planDetails]);

  const handleCheckboxChange = (planUuid) => {
    if (!isEditMode) return;

    setUpdatedPlans((prevPlans) =>
      prevPlans.map((plan) =>
        plan.plan_uuid === planUuid
          ? {
              ...plan,
              plan_status: !plan.plan_status,
              features: plan.features.map((feature) => ({
                ...feature,
                is_available: !plan.plan_status,
              })),
            }
          : plan
      )
    );
  };

  const toggleFeature = (featureUuid, planUuid) => {
    if (!isEditMode) return;

    setUpdatedPlans((prevPlans) =>
      prevPlans.map((plan) =>
        plan.plan_uuid === planUuid && plan.plan_status
          ? {
              ...plan,
              features: plan.features.map((feature) =>
                feature.module_uuid === featureUuid
                  ? { ...feature, is_available: !feature.is_available }
                  : feature
              ),
            }
          : plan
      )
    );
  };

  const handleSubmit = () => {
    const invalidPlan = updatedPlans.some(
      (plan) =>
        plan.plan_status &&
        plan.features.every((feature) => !feature.is_available)
    );

    if (invalidPlan) {
      dispatch(
        showSnackbar({
          message: "At least one feature must be available for an active plan.",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      return;
    }

    const data = updatedPlans.map((plan) => ({
      plan_uuid: plan.plan_uuid,
      plan_name: plan.plan_name,
      plan_status: plan.plan_status,
      features: plan.features,
    }));

    const onSuccess = (res) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: res?.data?.status ? "Update successful" : "Update failed",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: res?.data?.status ? "success" : "error",
        })
      );
      setIsEditMode(false);
      handleProductChange({ target: { value: selectedProduct } });
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: "Server Error",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      console.log(err);
    };

    dispatch(showLoader());
    productMappingApi
      .updateProductData(data, uuid)
      .then(onSuccess)
      .catch(onFailure);
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      handleSubmit();
    } else {
      setIsEditMode(true);
    }
  };

  // Cancel function to exit edit mode
  const handleCancel = () => {
    setIsEditMode(false);
    // setUpdatedPlans(
    //   planDetails.map((plan) => ({
    //     plan_uuid: plan.plan_uuid,
    //     plan_name: plan.plan_name,
    //     plan_status: plan.plan_status,
    //     features: plan.features.map((feature) => ({
    //       module_uuid: feature.module_uuid,
    //       module_name: feature.module_name,
    //       is_available: feature.is_available,
    //     })),
    //   }))
    // );
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ margin: "auto", marginTop: "20px", maxWidth: "100%" }}>
        <Card sx={{ marginBottom: 2, width: "100%" }} elevation={3}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" gutterBottom>
                Product List - {selectedProduct}
              </Typography>
              {!isEditMode && isDisabled && (
                <IconButton onClick={toggleEditMode}>
                  <EditIcon />
                </IconButton>
              )}
            </Box>

            <div style={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ backgroundColor: "#f5f5f5" }}>
                      Modules/Features
                    </TableCell>
                    {Array.isArray(planDetails) &&
                      planDetails
                        .slice()
                        .reverse()
                        .map((plan) => (
                          <TableCell
                            key={plan.plan_uuid}
                            sx={{ backgroundColor: "#f5f5f5" }}
                          >
                            {plan.plan_name}
                            {isEditMode && (
                              <Checkbox
                                checked={
                                  updatedPlans.find(
                                    (p) => p.plan_uuid === plan.plan_uuid
                                  )?.plan_status || false
                                }
                                onChange={() =>
                                  handleCheckboxChange(plan.plan_uuid)
                                }
                                disabled={!isEditMode}
                              />
                            )}
                          </TableCell>
                        ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(commonFeatures) &&
                    commonFeatures.map((feature) => (
                      <TableRow key={feature.module_uuid}>
                        <TableCell>{feature.module_name}</TableCell>
                        {Array.isArray(planDetails) &&
                          planDetails
                            .slice()
                            .reverse()
                            .map((plan) => (
                              <TableCell
                                key={plan.plan_uuid}
                                sx={{
                                  textAlign: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {isEditMode ? (
                                  <Checkbox
                                    checked={
                                      updatedPlans
                                        .find(
                                          (p) => p.plan_uuid === plan.plan_uuid
                                        )
                                        ?.features.find(
                                          (f) =>
                                            f.module_uuid ===
                                            feature.module_uuid
                                        )?.is_available || false
                                    }
                                    onChange={() =>
                                      toggleFeature(
                                        feature.module_uuid,
                                        plan.plan_uuid
                                      )
                                    }
                                    disabled={
                                      !updatedPlans.find(
                                        (p) => p.plan_uuid === plan.plan_uuid
                                      )?.plan_status
                                    } // Disable if the plan is not active
                                  />
                                ) : updatedPlans
                                    .find((p) => p.plan_uuid === plan.plan_uuid)
                                    ?.features.find(
                                      (f) =>
                                        f.module_uuid === feature.module_uuid
                                    )?.is_available ? (
                                  <CheckIcon color="success" />
                                ) : (
                                  <CloseIcon color="error" />
                                )}
                              </TableCell>
                            ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>

            {isEditMode && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    marginRight: 2,
                    borderColor: "#ff4b2b",
                    color: "#ff4b2b",
                    "&:hover": {
                      borderColor: "#ff416c",
                      color: "#ff416c",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    background:
                      "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
                    color: "#fff",
                    borderRadius: "8px",
                    fontWeight: "bold",
                    "&:hover": {
                      background:
                        "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
                    },
                  }}
                >
                  Submit
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ProductList;
